import { PlantRow } from "@/app/components/PlantRow";
import { PotIllustration } from "@/app/components/PotIllustration";
import { PotWateringRecommendations } from "@/app/components/PotWateringRecommendations";
import { SpeciesCard } from "@/app/components/SpeciesCard";
import { Button } from "@/app/components/system/Button";
import { BackIcon, EditIcon, LeafIcon, PlantIcon, PlusIcon, SunIcon } from "@/app/components/system/Icons";
import { RoundButton } from "@/app/components/system/RoundButton";
import { potSizeShortLabel } from "@/app/lib/potSizes";
import { potCoverPhotoUrl } from "@/app/lib/plant-image";
import type { Plant, Pot } from "@/types";
import { useNavigate, useParams } from "react-router";
import { usePots } from "../lib/data-context";

export function PotDetailScreen() {
  const navigate = useNavigate();
  const { id: potId } = useParams<{ id: string }>();
  const { pots } = usePots();
  const pot: Pot | undefined = pots.find((x) => x.id === potId);
  const plantsInPot: Plant[] = pot?.plants ?? [];
  if (!pot) return null;

  // A single-plant pot borrows that plant's photo as a full-bleed cover, and
  // surfaces the plant's own details (species card) in place of a plant list.
  const coverPhoto = potCoverPhotoUrl(pot);
  const solePlant = plantsInPot.length === 1 ? plantsInPot[0]! : null;

  return (
    <div className="screen pb-25 px-0 pt-0">
      <div
        className="relative h-55 grid place-items-center overflow-hidden"
        style={{ background: "linear-gradient(180deg, var(--moss-soft), var(--cream-2))" }}
      >
        {coverPhoto && (
          <img
            src={coverPhoto}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <RoundButton
          tone={coverPhoto ? "ghost" : "default"}
          className="absolute top-4.5 left-4.5"
          onClick={() => navigate("/")}
          aria-label="Back"
        >
          <BackIcon size={20} stroke={2} />
        </RoundButton>
        <RoundButton
          tone={coverPhoto ? "ghost" : "default"}
          className="absolute top-4.5 right-4.5"
          aria-label="Edit pot"
          onClick={() => navigate(`/pots/${pot.id}/edit`)}
        >
          <EditIcon size={18} />
        </RoundButton>
        {!coverPhoto && (
          <div style={{ filter: "drop-shadow(0 8px 16px rgba(40,30,10,.15))" }}>
            <PotIllustration shape={pot.shape} plants={plantsInPot} size="large" />
          </div>
        )}
        <div className="absolute left-0 right-0 bottom-0 flex h-3 [&>span]:flex-1 [&>span]:bg-[linear-gradient(180deg,transparent,rgba(0,0,0,.15))]">
          <span /><span /><span /><span />
        </div>
      </div>

      <div className="pt-5 px-5.5">
        <h1 className="display">{pot.name}</h1>
        <div className="mt-1 text-ink-3 text-[13px] flex gap-1 items-center">
          <p>{potSizeShortLabel(pot.size)}</p>
          <span>·</span>
          <span className="flex items-center gap-px">
            <SunIcon />
            {pot.sunHours?.start}-{pot.sunHours?.end}
          </span>
          <span>·</span>
          <span className="flex items-center gap-px">
            <PlantIcon />
            {plantsInPot.length}
          </span>
        </div>
        {plantsInPot.length === 0 ? (
          <EmptyPot
            potName={pot.name}
            onAdd={() => navigate(`/add?potId=${pot.id}`)}
          />
        ) : (
          <>
            <div className="mt-4">
              <PotWateringRecommendations pot={pot} days={5} />
            </div>

            {solePlant ? (
              // A single-plant pot shows that plant's details directly.
              <>
                <h3 className="section-h flex items-center justify-between gap-3">
                  {solePlant.nickname}
                  <button
                    onClick={() => navigate(`/plants/${solePlant.id}/edit`)}
                    className="inline-flex items-center gap-1.25 bg-moss-soft text-moss rounded-full pr-3 py-1.5 pl-2.5 font-sans text-xs font-medium cursor-pointer tracking-[0.01em] normal-case transition-[background] duration-150 border border-[oklch(from_var(--moss)_calc(l_+_0.18)_c_h)] hover:bg-[oklch(from_var(--moss-soft)_calc(l_-_0.04)_c_h)]"
                  >
                    <EditIcon size={13} /> Edit plant
                  </button>
                </h3>
                <SpeciesCard species={solePlant.species ?? null} />
                <button
                  onClick={() => navigate(`/add?potId=${pot.id}`)}
                  className="flex items-center justify-center gap-2 w-full px-3.5 py-3 border-[1.5px] border-dashed border-line rounded-[14px] bg-transparent text-moss font-sans text-[13.5px] font-medium cursor-pointer transition-[border-color,background,color] duration-150 hover:border-moss hover:bg-moss-soft"
                >
                  <span className="w-5.5 h-5.5 rounded-full grid place-items-center bg-moss-soft text-moss">
                    <PlusIcon size={14} stroke={2} />
                  </span>
                  <span>Add another plant</span>
                </button>
              </>
            ) : (
              <>
                <h3 className="section-h flex items-center justify-between gap-3">
                  Plants in this pot
                  <button
                    onClick={() => navigate(`/add?potId=${pot.id}`)}
                    className="inline-flex items-center gap-1.25 bg-moss-soft text-moss rounded-full pr-3 py-1.5 pl-2.5 font-sans text-xs font-medium cursor-pointer tracking-[0.01em] normal-case transition-[background] duration-150 border border-[oklch(from_var(--moss)_calc(l_+_0.18)_c_h)] hover:bg-[oklch(from_var(--moss-soft)_calc(l_-_0.04)_c_h)]"
                  >
                    <PlusIcon size={13} stroke={2} /> Add plant
                  </button>
                </h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-2">
                  {plantsInPot.map((p) => (
                    <li key={p.id}>
                      <PlantRow plant={p} onClick={() => navigate(`/plants/${p.id}`)} variant="card" />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface EmptyPotProps { potName: string; onAdd: () => void }
function EmptyPot({ potName, onAdd }: EmptyPotProps) {
  return (
    <div className="flex flex-col items-center text-center gap-2.5 px-5 pt-7.5 pb-6.5 mt-1.5 mb-1 bg-cream-2 border border-dashed border-line rounded-[20px]">
      <div className="relative w-18 h-18 grid place-items-center rounded-full bg-moss-soft text-moss mb-1" aria-hidden="true">
        <LeafIcon size={32} />
        <span className="absolute -top-1 -right-1 w-6.5 h-6.5 rounded-full bg-paper border border-line grid place-items-center text-moss">
          <PlusIcon size={14} stroke={2} />
        </span>
      </div>
      <h2 className="m-0 font-display font-medium text-[19px] tracking-tight text-ink">A new home, ready for life</h2>
      <p className="mt-0 mb-1.5 max-w-[34ch] text-[13.5px] text-ink-2 leading-normal">
        Scan a plant label and drop it into{" "}
        <em className="font-display italic text-ink">{potName}</em>.
        Plants in the same pot share soil, water, and a watering schedule.
      </p>
      <Button className="mt-0.5" onClick={onAdd}>
        <PlusIcon size={16} stroke={2} /> Add the first plant
      </Button>
    </div>
  );
}
